import { useRecoilState } from 'recoil';
import { previousHangingProtocolState } from './state';
import type { HangingProtocol } from './useHangingProtocol';
import { useResolvedSelectedHangingProtocol } from './useSelectedHangingProtocol';
import { useBaseViewerData } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { WithApolloContext } from 'modules/Apollo/types';

export type UsePreviousHangingProtocol = {
  previousHangingProtocol: Nullable<HangingProtocol>;
  savePreviousHangingProtocol: () => void;
};

export const usePreviousHangingProtocol = ({
  context,
}: WithApolloContext): UsePreviousHangingProtocol => {
  // Snapshot whatever is *currently active* for this case — not just the raw server match —
  // so "previous" reflects reality. The user's selected HP takes priority over the
  // server-matched HP here, mirroring the same non-preview priority chain useHangingProtocol uses
  // (selected ?? matched). Without this, cancelling a preview started after a
  // refresh would fall back to the server match instead of the selection (VX-4531).
  //
  // Reads the resolution rather than driving it: whichever component is fetching the selected
  // payload, this only needs the value once it is there, and a preview cannot be started before the
  // viewer has an HP applied.
  const { hangingProtocol: matchedHangingProtocol } = useBaseViewerData({ context });
  const selectedHangingProtocol = useResolvedSelectedHangingProtocol(matchedHangingProtocol);
  const [previousHangingProtocol, setPreviousHangingProtocol] = useRecoilState(
    previousHangingProtocolState
  );
  const savePreviousHangingProtocol = () =>
    setPreviousHangingProtocol(selectedHangingProtocol ?? matchedHangingProtocol);
  return {
    previousHangingProtocol,
    savePreviousHangingProtocol,
  };
};
